import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Button, TextField, 
  FormControl, InputLabel, Select, MenuItem, 
  CircularProgress, Alert, Tabs, Tab, Paper,
  IconButton, Card, CardMedia, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Snackbar
} from '@mui/material';

import Grid from '@mui/material/Grid';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Импорт богатого текстового редактора с поддержкой темы
import ThemedRichTextEditor from '../../components/common/ThemedRichTextEditor';

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`blog-tabpanel-${index}`}
      aria-labelledby={`blog-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const BlogPostForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isEditMode = Boolean(id);
  
  // Определяем тип контента по URL
  const currentPath = window.location.pathname;
  const contentType = currentPath.includes('/tips/') ? 'tip' : 'blog';
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    status: 'draft',
    contentType: contentType, // Определяется автоматически по URL
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
    // Поля для советов
    category: '',
    difficulty: '',
    estimatedTime: '',
    tags: ''
  });
  
  // Featured image state
  const [featuredImage, setFeaturedImage] = useState(null);
  const [featuredImagePreview, setFeaturedImagePreview] = useState('');
  
  // Additional images state
  const [images, setImages] = useState([]);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [deleteImageDialogOpen, setDeleteImageDialogOpen] = useState(false);
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Loading states for images
  const [featuredImageLoading, setFeaturedImageLoading] = useState(false);
  const [additionalImageLoading, setAdditionalImageLoading] = useState(false);
  const [uploadingImageName, setUploadingImageName] = useState('');
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Help dialog state
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  
  // Check if user is admin
  useEffect(() => {
    if (user && !['admin', 'superadmin'].includes(user.role)) {
      navigate('/');
    }
  }, [user, navigate]);
  
  // Fetch blog post data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchBlogPost();
    }
  }, [id]);
  
  // Fetch blog post
  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      console.log('Загружаем данные для редактирования, ID:', id, 'Тип контента:', contentType);
      
      // Определяем правильный API эндпоинт
      const apiEndpoint = contentType === 'tip' ? `/api/tips/${id}` : `/api/blog/${id}`;
      console.log('Используем API эндпоинт:', apiEndpoint);
      
      // Запрашиваем данные статьи
      const response = await axios.get(apiEndpoint);
      const post = response.data;
      console.log('Получены данные:', post);
      console.log('Поле featuredImage:', post.featuredImage);
      console.log('Все ключи объекта:', Object.keys(post));
      
      // Устанавливаем основные данные формы
      setFormData({
        title: post.title || '',
        content: post.content || '',
        excerpt: post.excerpt || '',
        status: post.status || 'draft',
        contentType: post.contentType || contentType,
        metaTitle: post.metaTitle || '',
        metaDescription: post.metaDescription || '',
        metaKeywords: post.metaKeywords || '',
        // Поля для советов
        category: post.category || '',
        difficulty: post.difficulty || '',
        estimatedTime: post.estimatedTime || '',
        tags: post.tags || ''
      });
      
      // Устанавливаем главное изображение
      if (post.featuredImage) {
        console.log('Устанавливаем главное изображение:', post.featuredImage);
        setFeaturedImagePreview(post.featuredImage);
      }
      
      // Проверяем разные варианты полей с дополнительными изображениями
      let blogImages = [];
      
      // Проверяем post.images
      if (post.images && post.images.length > 0) {
        console.log('Найдены изображения в post.images:', post.images.length);
        blogImages = post.images;
      }
      // Проверяем post.BlogImages
      else if (post.BlogImages && post.BlogImages.length > 0) {
        console.log('Найдены изображения в post.BlogImages:', post.BlogImages.length);
        blogImages = post.BlogImages;
      }
      // Проверяем post.TipImages (для советов)
      else if (post.TipImages && post.TipImages.length > 0) {
        console.log('Найдены изображения в post.TipImages:', post.TipImages.length);
        blogImages = post.TipImages;
      }
      
      // Если нашли изображения, устанавливаем их
      if (blogImages.length > 0) {
        // Приводим формат данных к единому виду
        const formattedImages = blogImages.map(img => ({
          id: img.id,
          url: img.url || img.imageUrl,
          imageUrl: img.imageUrl || img.url,
          caption: img.caption || img.altText || '',
          altText: img.altText || img.caption || '',
          isTemporary: false // Изображения из базы - постоянные
        }));
        setImages(formattedImages);
      } else {
        console.log('Дополнительные изображения не найдены');
      }
      
      // Если изображения не были найдены, пробуем загрузить их отдельным запросом
      if (blogImages.length === 0) {
        try {
          console.log('Пытаемся загрузить изображения отдельным запросом');
          const imageEndpoint = contentType === 'tip' ? 'tips' : 'blog';
          const imagesResponse = await axios.get(`/api/${imageEndpoint}/${id}/images`);
          if (imagesResponse.data && imagesResponse.data.length > 0) {
            console.log('Получены изображения отдельным запросом:', imagesResponse.data);
            const formattedImages = imagesResponse.data.map(img => ({
              id: img.id,
              url: img.url || img.imageUrl,
              imageUrl: img.imageUrl || img.url,
              caption: img.caption || img.altText || '',
              altText: img.altText || img.caption || '',
              isTemporary: false // Изображения из базы - постоянные
            }));
            setImages(formattedImages);
          }
        } catch (err) {
          console.error('Ошибка при загрузке дополнительных изображений:', err);
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching content:', err);
      const contentName = contentType === 'tip' ? 'совета' : 'статьи';
      setError(`Не удалось загрузить данные ${contentName}`);
      setLoading(false);
    }
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle content change
  const handleContentChange = (value) => {
    setFormData(prev => ({ ...prev, content: value }));
  };
  
  // Handle featured image change
  const handleFeaturedImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFeaturedImage(file);
      setUploadingImageName(file.name);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setFeaturedImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('=== НАЧАЛО ОТПРАВКИ ФОРМЫ ===');
    console.log('Данные формы:', formData);
    console.log('Режим редактирования:', isEditMode);
    console.log('ID поста (если редактирование):', id);
    console.log('Есть главное изображение:', !!featuredImage);
    
    // Validate form
    if (!formData.title || !formData.content) {
      console.error('Ошибка валидации: отсутствует заголовок или содержание');
      setSaveError('Заголовок и содержание обязательны');
      return;
    }
    
    try {
      setSaveLoading(true);
      setSaveError(null);
      
      // Создаем обычный объект вместо FormData для основных данных
      const postData = { ...formData };
      console.log('Подготовленные данные для отправки:', postData);
      
      // Определяем API эндпоинты на основе типа контента
      const apiEndpoint = formData.contentType === 'tip' ? '/api/tips' : '/api/blog';
      const imageEndpoint = formData.contentType === 'tip' ? 'tips' : 'blog';
      
      let response;
      
      if (isEditMode) {
        console.log('Режим редактирования: обновляем существующий пост');
        
        // Если есть новое изображение, отправляем его отдельно
        if (featuredImage) {
          console.log('Загружаем новое главное изображение...');
          const imageFormData = new FormData();
          imageFormData.append('featuredImage', featuredImage);
          console.log('Информация об изображении:', {
            name: featuredImage.name,
            size: featuredImage.size,
            type: featuredImage.type
          });
          
          try {
            // Сначала загружаем изображение
            const imageResponse = await axios.post(`/api/${imageEndpoint}/${id}/featured-image`, imageFormData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
            console.log('Изображение успешно загружено:', imageResponse.data);
          } catch (imageError) {
            console.error('Ошибка при загрузке изображения:', imageError);
            console.error('Детали ошибки:', imageError.response?.data || imageError.message);
            throw new Error('Ошибка при загрузке изображения: ' + (imageError.response?.data?.message || imageError.message));
          }
        }
        
        console.log('Отправляем данные поста...');
        try {
          // Затем отправляем текстовые данные как JSON
          response = await axios.put(`${apiEndpoint}/${id}`, postData);
          console.log('Пост успешно обновлен:', response.data);
        } catch (postError) {
          console.error('Ошибка при обновлении поста:', postError);
          console.error('Детали ошибки:', postError.response?.data || postError.message);
          throw new Error('Ошибка при обновлении поста: ' + (postError.response?.data?.message || postError.message));
        }
      } else {
        console.log('Режим создания: создаем новый контент');
        try {
          // Для нового контента сначала создаем запись
          response = await axios.post(apiEndpoint, postData);
          console.log('Новый пост успешно создан:', response.data);
          
          // Если есть изображение и пост успешно создан, загружаем изображение
          if (featuredImage && response.data && response.data.id) {
            const newPostId = response.data.id;
            console.log('Загружаем главное изображение для нового поста ID:', newPostId);
            
            const imageFormData = new FormData();
            imageFormData.append('featuredImage', featuredImage);
            console.log('Информация об изображении:', {
              name: featuredImage.name,
              size: featuredImage.size,
              type: featuredImage.type
            });
            
            setFeaturedImageLoading(true);
            try {
              const imageResponse = await axios.post(`/api/${imageEndpoint}/${newPostId}/featured-image`, imageFormData, {
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              });
              console.log('Изображение успешно загружено:', imageResponse.data);
              setFeaturedImageLoading(false);
            } catch (imageError) {
              console.error('Ошибка при загрузке изображения:', imageError);
              console.error('Детали ошибки:', imageError.response?.data || imageError.message);
              setFeaturedImageLoading(false);
              // Продолжаем выполнение, так как пост уже создан
            }
          }
          
          // Переносим временные изображения к созданной статье
          if (response.data && response.data.id && images.length > 0) {
            const newPostId = response.data.id;
            const tempImages = images.filter(img => img.isTemporary);
            
            if (tempImages.length > 0) {
              console.log(`Переносим ${tempImages.length} временных изображений к контенту ID: ${newPostId}`);
              console.log('Временные изображения:', tempImages.map(img => ({
                url: img.url || img.imageUrl,
                isTemporary: img.isTemporary
              })));
              
              try {
                const tempImageUrls = tempImages.map(img => img.url || img.imageUrl);
                console.log('URL временных изображений для переноса:', tempImageUrls);
                
                await axios.post(`/api/${imageEndpoint}/${newPostId}/move-temp-images`, {
                  tempImageIds: tempImageUrls
                });
                console.log('Временные изображения успешно перенесены');
              } catch (moveError) {
                console.error('Ошибка при переносе временных изображений:', moveError);
                // Не прерываем выполнение, так как статья уже создана
              }
            }
          }
        } catch (postError) {
          console.error('Ошибка при создании поста:', postError);
          console.error('Детали ошибки:', postError.response?.data || postError.message);
          throw new Error('Ошибка при создании поста: ' + (postError.response?.data?.message || postError.message));
        }
      }
      
      console.log('Сохранение завершено успешно');
      setSaveLoading(false);
      setSaveSuccess(true);
      
      // Определяем правильный маршрут для редиректа
      const redirectPath = formData.contentType === 'tip' ? '/admin/tips' : '/admin/blog';
      console.log('Перенаправление на', redirectPath, 'через 1.5 секунды...');
      setTimeout(() => {
        navigate(redirectPath);
      }, 1500);
    } catch (err) {
      console.error('=== ОШИБКА ПРИ СОХРАНЕНИИ ===');
      console.error(err);
      setSaveError(err.message || 'Произошла неизвестная ошибка при сохранении');
      setSaveLoading(false);
    } finally {
      console.log('=== КОНЕЦ ОТПРАВКИ ФОРМЫ ===');
    }
  };
  
  // Open delete image dialog
  const openDeleteImageDialog = (image) => {
    setImageToDelete(image);
    setDeleteImageDialogOpen(true);
  };
  
  // Close delete image dialog
  const closeDeleteImageDialog = () => {
    setDeleteImageDialogOpen(false);
    setImageToDelete(null);
  };
  
  // Copy image URL to clipboard
  const copyImageUrl = (url, caption) => {
    console.log('Копируем URL изображения:', url);
    
    if (!url) {
      console.error('Ошибка: URL изображения отсутствует');
      setSnackbarMessage('Ошибка: URL изображения отсутствует');
      setSnackbarOpen(true);
      return;
    }
    
    // Копируем просто URL изображения
    navigator.clipboard.writeText(url)
      .then(() => {
        console.log('URL изображения успешно скопирован:', url);
        setSnackbarMessage('URL изображения скопирован в буфер обмена');
        setSnackbarOpen(true);
      })
      .catch(err => {
        console.error('Ошибка при копировании URL:', err);
        setSnackbarMessage('Не удалось скопировать URL');
        setSnackbarOpen(true);
      });
  };
  
  // Close snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  
  // Open help dialog
  const openHelpDialog = () => {
    setHelpDialogOpen(true);
  };
  
  // Close help dialog
  const closeHelpDialog = () => {
    setHelpDialogOpen(false);
  };
  
  // Delete image
  const deleteImage = async () => {
    if (!imageToDelete) return;
    
    try {
      const apiEndpoint = formData.contentType === 'tip' ? 'tips' : 'blog';
      await axios.delete(`/api/${apiEndpoint}/images/${imageToDelete.id}`);
      setImages(images.filter(img => img.id !== imageToDelete.id));
      setSnackbarMessage('Изображение успешно удалено');
      setSnackbarOpen(true);
      closeDeleteImageDialog();
    } catch (err) {
      console.error('Error deleting image:', err);
      setSnackbarMessage('Ошибка при удалении изображения: ' + (err.response?.data?.message || err.message));
      setSnackbarOpen(true);
      closeDeleteImageDialog();
    }
  };
  
  // Add new image
  const handleAddImage = async (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    const newImageFormData = new FormData();
    newImageFormData.append('image', file);
    
    const contentName = formData.contentType === 'tip' ? 'совета' : 'статьи';
    const apiEndpoint = formData.contentType === 'tip' ? 'tips' : 'blog';
    
    console.log('Загрузка дополнительного изображения:', isEditMode ? `для ${contentName} ID: ${id}` : `для нового ${contentName}`);
    console.log('Используем API эндпоинт:', apiEndpoint);
    console.log('Информация о файле: ', {
      name: file.name,
      size: file.size,
      type: file.type
    });
    
    // Устанавливаем состояние загрузки
    setAdditionalImageLoading(true);
    setUploadingImageName(file.name);
    
    try {
      let response;
      
      if (isEditMode) {
        // Для существующего контента - загружаем к конкретному элементу
        response = await axios.post(`/api/${apiEndpoint}/${id}/images`, newImageFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // Для нового контента - загружаем во временное хранилище
        response = await axios.post(`/api/${apiEndpoint}/temp-images`, newImageFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      console.log('Ответ сервера при загрузке изображения: ', response.data);
      
      // Проверяем наличие URL изображения в ответе
      if (!response.data || (!response.data.url && !response.data.imageUrl)) {
        console.error('Ошибка: В ответе сервера отсутствует URL изображения');
        setSnackbarMessage('Ошибка: В ответе сервера отсутствует URL изображения');
        setSnackbarOpen(true);
        setAdditionalImageLoading(false);
        return;
      }
      
      // Создаем объект изображения с правильной структурой
      const newImage = {
        ...response.data,
        // Если в ответе есть imageUrl, используем его как url
        url: response.data.imageUrl || response.data.url,
        // Для новых статей добавляем флаг что это временное изображение
        isTemporary: !isEditMode
      };
      
      console.log('Добавляем изображение в состояние:', newImage);
      setImages([...images, newImage]);
      setSnackbarMessage('Изображение успешно загружено');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Ошибка при загрузке изображения:', err);
      setSnackbarMessage('Ошибка при загрузке изображения: ' + (err.response?.data?.message || err.message));
      setSnackbarOpen(true);
    } finally {
      setAdditionalImageLoading(false);
      setUploadingImageName('');
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => {
            const backPath = contentType === 'tip' ? '/admin/tips' : '/admin/blog';
            navigate(backPath);
          }}
          sx={{ mt: 2 }}
        >
          Вернуться к списку {contentType === 'tip' ? 'советов' : 'статей'}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          {isEditMode 
            ? `Редактирование ${formData.contentType === 'tip' ? 'совета' : 'статьи'}`
            : `${formData.contentType === 'tip' ? 'Новый совет' : 'Новая статья'}`
          }
        </Typography>
        <Box>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => {
              const backPath = formData.contentType === 'tip' ? '/admin/tips' : '/admin/blog';
              navigate(backPath);
            }}
            sx={{ mr: 1 }}
          >
            Отмена
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={saveLoading}
          >
            {saveLoading ? <CircularProgress size={24} /> : 'Сохранить'}
          </Button>
        </Box>
      </Box>
      
      {saveError && (
        <Alert severity="error" sx={{ mb: 3 }}>{saveError}</Alert>
      )}
      
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>Статья успешно сохранена</Alert>
      )}
      
      <Paper sx={{ mb: 4 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="blog post tabs">
          <Tab label="Основное" id="blog-tab-0" aria-controls="blog-tabpanel-0" />
          <Tab label="SEO" id="blog-tab-1" aria-controls="blog-tabpanel-1" />
          <Tab label="Изображения" id="blog-tab-2" aria-controls="blog-tabpanel-2" />
        </Tabs>
        
        {/* Основная информация */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Заголовок"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Краткое описание"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <ThemedRichTextEditor 
                value={formData.content} 
                onChange={handleContentChange}
                placeholder={formData.contentType === 'tip' 
                  ? "Начните писать ваш полезный совет..."
                  : "Начните писать вашу статью..."
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="content-type-label">Тип контента</InputLabel>
                <Select
                  labelId="content-type-label"
                  name="contentType"
                  value={formData.contentType}
                  onChange={handleInputChange}
                  label="Тип контента"
                >
                  <MenuItem value="blog">Блог</MenuItem>
                  <MenuItem value="tip">Полезные советы</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Статус</InputLabel>
                <Select
                  labelId="status-label"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  label="Статус"
                >
                  <MenuItem value="draft">Черновик</MenuItem>
                  <MenuItem value="published">Опубликовано</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Поля специфичные для советов */}
            {formData.contentType === 'tip' && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="category-label">Категория</InputLabel>
                    <Select
                      labelId="category-label"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      label="Категория"
                    >
                      <MenuItem value="sauna">Баня</MenuItem>
                      <MenuItem value="health">Здоровье</MenuItem>
                      <MenuItem value="rental">Аренда</MenuItem>
                      <MenuItem value="outdoor">Отдых на природе</MenuItem>
                      <MenuItem value="maintenance">Обслуживание домика</MenuItem>
                      <MenuItem value="cooking">Готовка и кухня</MenuItem>
                      <MenuItem value="cleaning">Уборка</MenuItem>
                      <MenuItem value="heating">Отопление</MenuItem>
                      <MenuItem value="garden">Сад и огород</MenuItem>
                      <MenuItem value="entertainment">Развлечения</MenuItem>
                      <MenuItem value="safety">Безопасность</MenuItem>
                      <MenuItem value="other">Другое</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="difficulty-label">Сложность</InputLabel>
                    <Select
                      labelId="difficulty-label"
                      name="difficulty"
                      value={formData.difficulty}
                      onChange={handleInputChange}
                      label="Сложность"
                    >
                      <MenuItem value="easy">Легко</MenuItem>
                      <MenuItem value="medium">Средне</MenuItem>
                      <MenuItem value="hard">Сложно</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Время выполнения"
                    name="estimatedTime"
                    value={formData.estimatedTime}
                    onChange={handleInputChange}
                    placeholder="Например: 30 минут"
                    helperText="Примерное время выполнения совета"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Теги"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    placeholder="баня, здоровье, отдых, природа"
                    helperText="Теги через запятую"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </TabPanel>
        
        {/* SEO */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Meta Title"
                name="metaTitle"
                value={formData.metaTitle}
                onChange={handleInputChange}
                helperText="Если не указано, будет использован заголовок статьи"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Meta Description"
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleInputChange}
                multiline
                rows={3}
                helperText="Если не указано, будет использовано краткое описание статьи"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Meta Keywords"
                name="metaKeywords"
                value={formData.metaKeywords}
                onChange={handleInputChange}
                helperText="Ключевые слова через запятую"
              />
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Изображения */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Главное изображение
              </Typography>
              <Box 
                sx={{ 
                  border: '1px dashed #ccc', 
                  borderRadius: 1, 
                  p: 2, 
                  textAlign: 'center',
                  mb: 2
                }}
              >
                {featuredImagePreview ? (
                  <Box sx={{ position: 'relative' }}>
                    <img 
                      src={featuredImagePreview} 
                      alt="Featured" 
                      style={{ maxWidth: '100%', maxHeight: '200px' }} 
                    />
                    <Button 
                      variant="contained" 
                      color="primary"
                      component="label"
                      startIcon={<ImageIcon />}
                      sx={{ mt: 2 }}
                    >
                      Изменить
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleFeaturedImageChange}
                        disabled={featuredImageLoading}
                      />
                    </Button>
                  </Box>
                ) : (
                  <Button 
                    variant="contained" 
                    color="primary"
                    component="label"
                    startIcon={<ImageIcon />}
                  >
                    Загрузить
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleFeaturedImageChange}
                      disabled={featuredImageLoading}
                    />
                  </Button>
                )}
                {featuredImageLoading && (
                  <CircularProgress size={20} sx={{ position: 'absolute', top: 8, right: 8 }} />
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Дополнительные изображения
                </Typography>
                <Box sx={{ mb: 2, position: 'relative' }}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    component="label"
                    startIcon={additionalImageLoading ? <CircularProgress size={20} color="inherit" /> : <AddPhotoAlternateIcon />}
                    disabled={additionalImageLoading}
                  >
                    {additionalImageLoading 
                      ? `Загрузка ${uploadingImageName ? `"${uploadingImageName}"` : ''}...` 
                      : 'Добавить изображение'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleAddImage}
                      disabled={additionalImageLoading}
                    />
                  </Button>
                </Box>
                
                {/* Дополнительные изображения - только для постоянных изображений из базы */}

                {isEditMode && images.filter(img => !img.isTemporary).length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Дополнительные изображения
                    </Typography>
                    <Grid container spacing={1} sx={{ mb: 3 }}>
                      {images.filter(img => !img.isTemporary).map((image) => (
                        <Grid item xs={6} sm={4} key={image.id}>
                          <Card sx={{ position: 'relative' }}>
                            <CardMedia
                              component="img"
                              height="150"
                              image={image.url || image.imageUrl}
                              alt={image.altText || 'Additional image'}
                              sx={{ objectFit: 'cover' }}

                            />
                            <Box sx={{ 
                              position: 'absolute', 
                              top: 4, 
                              right: 4, 
                              display: 'flex', 
                              gap: 0.5,
                              backgroundColor: 'rgba(0,0,0,0.7)',
                              borderRadius: 1,
                              p: 0.5
                            }}>
                              <Tooltip title="Копировать URL">
                                <IconButton 
                                  size="small" 
                                  sx={{ color: 'white', p: 0.5 }}
                                  onClick={() => copyImageUrl(image.url || image.imageUrl, image.caption || image.altText)}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Удалить">
                                <IconButton 
                                  size="small" 
                                  sx={{ color: 'white', p: 0.5 }}
                                  onClick={() => openDeleteImageDialog(image)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
                
                {/* Временные изображения - только для новых постов или изображений из редактора */}
                {images.filter(img => img.isTemporary).length > 0 && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2">Изображения для вставки в текст</Typography>
                      <Tooltip title="Как вставить изображение в текст">
                        <IconButton size="small" onClick={openHelpDialog}>
                          <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    
                    <Grid container spacing={2}>
                      {images.filter(img => img.isTemporary).map((image) => (
                        <Grid item xs={12} sm={6} key={image.id}>
                          <Card>
                            <CardMedia
                              component="img"
                              height="140"
                              image={image.url || image.imageUrl}
                              alt={image.altText || 'Temporary image'}
                            />
                            <CardContent sx={{ p: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" noWrap>
                                  {image.caption || 'Временное изображение'}
                                </Typography>
                                <Box>
                                  <Tooltip title="Копировать URL изображения">
                                    <IconButton 
                                      size="small" 
                                      color="primary"
                                      onClick={() => copyImageUrl(image.url || image.imageUrl, image.caption || image.altText)}
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Удалить изображение">
                                    <IconButton 
                                      size="small" 
                                      color="error"
                                      onClick={() => openDeleteImageDialog(image)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
              </Grid>
          </Grid>
        </TabPanel>
      </Paper>
      
      {/* Диалог подтверждения удаления изображения */}
      <Dialog open={deleteImageDialogOpen} onClose={closeDeleteImageDialog}>
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography>
            Вы действительно хотите удалить это изображение?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteImageDialog}>
            Отмена
          </Button>
          <Button onClick={deleteImage} color="error" autoFocus>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Диалог с инструкциями по вставке изображений */}
      <Dialog open={helpDialogOpen} onClose={closeHelpDialog} maxWidth="md">
        <DialogTitle>Как вставить изображение в текст</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Для вставки изображения в текст статьи:
          </Typography>
          <Typography variant="h6" gutterBottom>
            Метод 1: Использование кнопки вставки изображения
          </Typography>
          <ol>
            <li>
              <Typography paragraph>
                Перейдите на вкладку "Основное" и установите курсор в редакторе в том месте, где должно быть изображение
              </Typography>
            </li>
            <li>
              <Typography paragraph>
                Нажмите кнопку вставки изображения в панели инструментов редактора или кнопку "Изображение по URL"
              </Typography>
            </li>
            <li>
              <Typography paragraph>
                Введите URL изображения и настройте параметры (размер, выравнивание, подпись)
              </Typography>
            </li>
          </ol>
          <Typography variant="h6" gutterBottom>
            Метод 2: Копирование URL из галереи
          </Typography>
          <ol>
            <li>
              <Typography paragraph>
                Нажмите на кнопку копирования рядом с нужным изображением (URL будет скопирован в буфер обмена)
              </Typography>
            </li>
            <li>
              <Typography paragraph>
                Перейдите на вкладку "Основное" и установите курсор в редакторе в том месте, где должно быть изображение
              </Typography>
            </li>
            <li>
              <Typography paragraph>
                Нажмите кнопку "Изображение по URL" и вставьте скопированный URL
              </Typography>
            </li>
          </ol>
          <Typography paragraph>
            Изображение будет автоматически отображаться в тексте статьи при просмотре.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeHelpDialog}>Понятно</Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar для уведомлений */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default BlogPostForm;
